import { OUTPASS_SENT_BY_LABELS, OutpassSentByMode } from 'shared/models';

/** The Sent by fields of an out pass that the one-line text reads. */
export interface OutpassSentByFields {
  SentByMode?: OutpassSentByMode | null;
  TransportName?: string | null;
  VehicleId?: number | null;
  VehicleNumber?: string | null;
  CourierName?: string | null;
  DocketNumber?: string | null;
  CarrierName?: string | null;
  CarrierContact?: string | null;
}

const clean = (value: string | null | undefined): string => (value ?? '').trim();

/** The chosen mode, or Vehicle for older out passes that have a vehicle but no mode; null when nothing is known. */
function effectiveMode(outpass: OutpassSentByFields): OutpassSentByMode | null {
  if (outpass.SentByMode) {
    return outpass.SentByMode;
  }
  return outpass.VehicleId > 0 || clean(outpass.VehicleNumber) ? 'Vehicle' : null;
}

function detailsFor(mode: OutpassSentByMode, outpass: OutpassSentByFields): string {
  switch (mode) {
    case 'Vehicle':
      return [clean(outpass.TransportName), clean(outpass.VehicleNumber)].filter(Boolean).join(' / ');
    case 'Courier': {
      const name = clean(outpass.CourierName);
      const docket = clean(outpass.DocketNumber);
      return name && docket ? `${name} (Docket ${docket})` : name;
    }
    case 'ByHand': {
      const name = clean(outpass.CarrierName);
      const contact = clean(outpass.CarrierContact);
      return name && contact ? `${name} (${contact})` : name;
    }
  }
}

/** One line telling how an out pass leaves, e.g. "Courier — DTDC (Docket 12345)"; empty when nothing is known. */
export function sentByText(outpass: OutpassSentByFields | null | undefined): string {
  if (!outpass) {
    return '';
  }
  const mode = effectiveMode(outpass);
  if (!mode) {
    return '';
  }
  const label = OUTPASS_SENT_BY_LABELS[mode] ?? mode;
  const details = detailsFor(mode, outpass);
  return details ? `${label} — ${details}` : label;
}

/** The shown title of a timeline entry: 'SentByUpdated' reads "Sent by updated"; every other status is unchanged. */
export function timelineStatusLabel(status: string): string {
  return status === 'SentByUpdated' ? 'Sent by updated' : status;
}
